import React, { useState } from 'react';
import { X, Globe, ArrowRight, ArrowLeft, SkipForward, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls
const API = '/api';

const SECTIONS = [
  {
    id: 'intro',
    title: 'Welcome!',
    description: 'Let\'s create a beautiful website for your guests. We\'ll go through this step-by-step.',
    isIntro: true
  },
  {
    id: 'couple_names',
    field: 'url_slug',
    title: 'Your Names',
    label: 'Names',
    description: 'How should your names appear on the website?',
    placeholder: 'e.g., Sarah & Michael',
    example: '"Emily & David" or "The Johnsons"'
  },
  {
    id: 'love_story',
    field: 'couple_story',
    title: 'Your Love Story',
    label: 'Love Story',
    description: 'Share how you met, your journey together, or what makes your love special.',
    placeholder: 'Tell your story...',
    example: 'How you met, first date, proposal story, or what you love about each other',
    multiline: true
  },
  {
    id: 'ceremony',
    field: 'ceremony_details',
    title: 'Ceremony Details',
    label: 'Ceremony Information',
    description: 'Where and when is your ceremony?',
    placeholder: 'e.g., St. Mary\'s Church, 123 Main St, June 15th at 3:00 PM',
    example: 'Venue name, address, date, and time'
  },
  {
    id: 'reception',
    field: 'reception_details',
    title: 'Reception Details',
    label: 'Reception Information',
    description: 'Where will the celebration continue?',
    placeholder: 'e.g., The Grand Ballroom, 456 Oak Ave, following ceremony',
    example: 'Venue name, address, time (if different from ceremony)'
  },
  {
    id: 'custom_message',
    field: 'custom_message',
    title: 'Custom Message',
    label: 'Message to Guests',
    description: 'Any special message or instructions for your guests?',
    placeholder: 'e.g., Dress code: Garden formal. Parking available on-site.',
    example: 'Dress code, parking info, hotel blocks, or special requests',
    multiline: true
  },
  {
    id: 'theme_color',
    field: 'theme_color',
    title: 'Website Theme',
    label: 'Theme Color',
    description: 'Choose a color that matches your wedding style.',
    placeholder: '#881337',
    example: 'Rose, Sage, Navy, Gold, Lavender - pick your wedding color!',
    isColor: true
  }
];

const WebsiteOnboarding = ({ isOpen, onClose, userId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [websiteData, setWebsiteData] = useState({
    url_slug: '',
    couple_story: '',
    ceremony_details: '',
    reception_details: '',
    custom_message: '',
    theme_color: '#881337'
  });

  const currentSection = SECTIONS[currentStep];
  const isLastStep = currentStep === SECTIONS.length - 1;

  const handleNext = () => {
    if (currentStep < SECTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (currentSection.isIntro) {
      handleNext();
      return;
    }
    // Skip current field and move to next
    handleNext();
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Ensure we have a valid url_slug
      let slug = websiteData.url_slug?.trim() || websiteData.couple_names?.trim() || '';
      
      // Generate slug from entered name or create default
      if (slug) {
        slug = slug.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      
      if (!slug) {
        slug = `wedding-${Date.now()}`;
      }
      
      // Map fields correctly for API
      const apiData = {
        url_slug: slug,
        couple_story: (websiteData.couple_story || websiteData.love_story || '').trim(),
        ceremony_details: (websiteData.ceremony_details || '').trim(),
        reception_details: (websiteData.reception_details || '').trim(),
        custom_message: (websiteData.custom_message || '').trim(),
        theme_color: websiteData.theme_color || '#881337',
        user_id: userId
      };

      await axios.post(`${API}/guest-websites`, apiData, {
        headers: { 'user-id': userId }
      });

      toast.success('Wedding website created! 🎉');
      onClose(true, false); // completed=true, skipped=false
    } catch (error) {
      console.error('Website creation error:', error);
      
      // Handle Pydantic validation errors properly
      let errorMessage = 'Failed to create website';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation error - extract messages
          errorMessage = error.response.data.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('; ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipAll = () => {
    toast.success('You can create your website anytime from the dashboard');
    onClose(false, true); // completed=false, skipped=true
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-stone-200 bg-gradient-to-r from-[#881337] to-[#9F1239]">
          <div className="flex items-center gap-3">
            <Globe className="text-white" size={28} />
            <div>
              <h2 className="text-2xl font-serif font-bold text-white">Create Your Wedding Website</h2>
              <p className="text-white/80 text-sm">Step {currentStep + 1} of {SECTIONS.length}</p>
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="bg-stone-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-[#881337] h-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / SECTIONS.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          {currentSection.isIntro ? (
            <div className="space-y-6">
              <div className="bg-stone-50 rounded-xl p-6 border-2 border-dashed border-stone-300">
                <h3 className="text-xl font-semibold text-[#1C1917] mb-4">Your Wedding Website</h3>
                <p className="text-stone-700 mb-4">
                  We'll help you create a beautiful website where guests can:
                </p>
                <ul className="space-y-2 text-sm text-stone-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#881337] flex-shrink-0 mt-0.5" />
                    Read your love story
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#881337] flex-shrink-0 mt-0.5" />
                    Find ceremony and reception details
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#881337] flex-shrink-0 mt-0.5" />
                    RSVP online
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#881337] flex-shrink-0 mt-0.5" />
                    See important updates
                  </li>
                </ul>
                <p className="text-sm text-stone-500 italic mt-4">
                  💡 We'll guide you through each section. You can always update it later!
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSkipAll}
                  className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700"
                >
                  Skip for Now
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white flex items-center justify-center gap-2"
                >
                  Let's Get Started
                  <ArrowRight size={18} />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <h3 className="text-xl font-semibold text-[#1C1917] mb-2">{currentSection.title}</h3>
                <p className="text-sm text-stone-600 mb-4">{currentSection.description}</p>
                <p className="text-xs text-stone-500 italic mb-4">Example: {currentSection.example}</p>
              </div>

              {currentSection.isColor ? (
                <div>
                  <label className="text-[#1C1917] font-medium mb-2 block">Choose Your Color</label>
                  <div className="grid grid-cols-5 gap-3 mb-4">
                    {['#881337', '#6B7280', '#059669', '#0284C7', '#7C3AED'].map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setWebsiteData({ ...websiteData, theme_color: color })}
                        className={`w-full aspect-square rounded-xl border-4 transition-all ${
                          websiteData.theme_color === color
                            ? 'border-[#1C1917] scale-110'
                            : 'border-stone-300 hover:border-stone-400'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <Input
                    value={websiteData.theme_color}
                    onChange={(e) => setWebsiteData({ ...websiteData, theme_color: e.target.value })}
                    placeholder="#881337"
                    className="border-stone-300"
                  />
                </div>
              ) : currentSection.multiline ? (
                <textarea
                  value={websiteData[currentSection.field] || ''}
                  onChange={(e) => setWebsiteData({ ...websiteData, [currentSection.field]: e.target.value })}
                  placeholder={currentSection.placeholder}
                  rows={5}
                  className="w-full p-4 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
                />
              ) : (
                <Input
                  value={websiteData[currentSection.field] || ''}
                  onChange={(e) => setWebsiteData({ ...websiteData, [currentSection.field]: e.target.value })}
                  placeholder={currentSection.placeholder}
                  className="border-stone-300"
                />
              )}

              <div className="flex gap-3 pt-4">
                {currentStep > 0 && (
                  <Button
                    onClick={handleBack}
                    className="bg-stone-200 hover:bg-stone-300 text-stone-700 flex items-center gap-2"
                  >
                    <ArrowLeft size={18} />
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleSkip}
                  className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700 flex items-center justify-center gap-2"
                >
                  <SkipForward size={18} />
                  Skip This
                </Button>
                <Button
                  onClick={isLastStep ? handleComplete : handleNext}
                  disabled={isLastStep && loading}
                  className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white flex items-center justify-center gap-2"
                >
                  {isLastStep ? (loading ? 'Creating...' : 'Finish') : 'Next'}
                  {!isLastStep && <ArrowRight size={18} />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebsiteOnboarding;